import React, { useEffect, useState } from 'react';
import {
    Card, CardBody, Col, Modal, ModalBody, ModalHeader, Row, Table,
} from 'reactstrap';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { ProjectLicenseList } from '../../../../domain/projectLicenses/ProjectLicenseList';
import { CreateProjectLicensesModel } from '../../../../domain/projectLicenses/CreateProjectLicensesModel';
import ModalCreateProjectLicensesSection from '../form/ModalCreateProjectLicensesSection';
import { LicensesRepository } from '../../../../infrastructure/repositories/licenses/LicencesRepository';

interface ProjectLicensesTabProps {
    data: ProjectLicenseList[];
    onSortingChange?: (columnName: string, sortOrder: 'asc' | 'desc') => void;
    sortedBy?: string | null;
    ascending?: boolean | null;
    onRefresh?: () => void; 
}

const ProjectLicensesTab = ({
    data: projectLicensesList,
    onRefresh
}: ProjectLicensesTabProps) => {
    const initialState: CreateProjectLicensesModel = {
        productId: 0,
        quantity: 0,
        expirationDate: new Date(),
    };

    const { id } = useParams<{ id: any }>();

    const [licenses, setLicenses] = useState<ProjectLicenseList[]>(projectLicensesList);
    const [modal, setModal] = useState(false);
    const [createProjectLicenses, setProjectLicensesChanges] = useState<CreateProjectLicensesModel>(initialState);
    const [errorMessage, setErrorMessage] = useState<string>('');

    useEffect(() => {
        setLicenses(projectLicensesList);
    }, [projectLicensesList]);

    const refreshLicenses = async () => {
        try {
            onRefresh?.();
        } catch (error) {
            Swal.fire('Error', 'No se pudieron cargar las licencias', 'error');
        }
    };

    const UpdateLicenseStatus = (licenseId: number, currentStatus: number) => {
        if (currentStatus === 0) {
            Swal.fire('Error','No se puede activar una licencia sin asignar.','error');
            return;
        }
        
        const action = currentStatus === 1  
            ? LicensesRepository.InactivateProjectLicense : currentStatus === 2 
            ? LicensesRepository.ActivateProjectLicense : null;
        
        if(!action){
            Swal.fire('Error', 'Estado de licencia no válido.', 'error');
            return;
        }

        action(licenseId)
            .then(() => {
                Swal.fire('Estado actualizado', 'La licencia ha sido actualizada correctamente.', 'success');
                refreshLicenses();
            })
            .catch((error) => {
                const errorMessage = error?.response?.data || 'Ocurrió un error al actualizar la licencia.';
                Swal.fire('Error', errorMessage, 'error');
            });   
    };

    const validateForm = (): boolean => {
        const { productId, quantity, expirationDate } = createProjectLicenses;
        const date = new Date(expirationDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (!productId || productId === 0) return setError('Debe seleccionar un producto.');
        if (!quantity || quantity <= 0) return setError('La cantidad debe ser mayor a 0.');
        if (isNaN(date.getTime())) return setError('La fecha de expiración no es válida.');
        if (date <= today) return setError('La fecha de expiración debe ser posterior a hoy.');

        setErrorMessage('');
        return true;
    };

    const setError = (message: string): boolean => {
        setErrorMessage(message);
        return false;
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!validateForm()) return;

        try {
            await LicensesRepository.CreateProjectLicenses(id, createProjectLicenses);
            Swal.fire('Licencias creadas exitosamente', '', 'success');
            setProjectLicensesChanges(initialState);
            setModal(false);
            refreshLicenses();
        } catch (error) {
            handleError(error);
        }
    };

    const donwloadCsv = async (event: React.FormEvent) => {
        try {
            const response = await LicensesRepository.DonwloadProjectLicenses(id);
            const blob = new Blob([response], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'licencias.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            Swal.fire('No se pudo descargar ', "--", 'error');
        }
    };

    const handleError = (error: any) => {
        setErrorMessage(error.response?.data ?? 'Error inesperado');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleInputChange = (
        name: string,
        value: string | boolean | number[] | number | Date | undefined
    ) => {
        setProjectLicensesChanges({ ...createProjectLicenses, [name]: value });
    };

    const handleCloseModal = () => {
        setModal(false);
        refreshLicenses(); 
    };

    return (
        <React.Fragment>
            <Card>
                <CardBody>
                    <div className='row p-4'>
                        <div className='col-10 d-flex justify-content-start p-1'>
                            <button type='button' className='btn btn-success me-2' onClick={() => setModal(true)}>
                                <i className='ri-add-box-fill me-1 align-bottom'></i> Agregar
                            </button>
                            <button type='button' className='btn btn-primary' onClick={donwloadCsv}>
                                Descargar CSV
                            </button>
                        </div>
                    </div>

                    <Row>
                        <Col lg={12}>
                            <div className='table-responsive table-card'>
                                <Table className='table-borderless align-middle mb-0'>
                                    <thead className='table-light'>
                                        <tr>
                                            <th>Producto</th>
                                            <th>Fecha</th>
                                            <th>Expiración</th>
                                            <th>Serial</th>
                                            <th>Estado</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {licenses.map((license) => {
                                            const isExpired = license.expirationDate && new Date(license.expirationDate) < new Date();

                                            return (
                                                <tr key={license.id}>
                                                    <td>{license.product}</td>
                                                    <td>{license.date ? new Date(license.date).toLocaleDateString() : 'N/A'}</td>
                                                    <td>{license.expirationDate ? new Date(license.expirationDate).toLocaleDateString() : 'N/A'}</td>
                                                    <td>{license.serialNumber}</td>
                                                    <td>
                                                    {isExpired ? (
                                                            <span className="badge bg-warning">Expirado</span>
                                                        ) : license.status === 0 ? (
                                                            <span className="badge bg-secondary">Sin asignar</span>
                                                        ) : (
                                                            <span className={`badge ${license.status === 1 ? 'bg-success' : 'bg-danger'}`}>
                                                                {license.status === 1 ? 'Activo' : 'Inactivo'}
                                                            </span>
                                                    )}
                                                    </td>
                                                    <td>
                                                        {isExpired || license.status === 0 ? (
                                                            <span className="text-muted">
                                                                {license.status === 0 ? 'Sin asignar' : 'Expirado'}
                                                            </span>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className={`btn ${license.status === 1 ? 'btn-danger' : 'btn-success'}`}
                                                                style={{ minWidth: '90px' }}
                                                                onClick={() => {
                                                                    const action = license.status === 1 ? 'desactivar' : 'activar';
                                                                    if (window.confirm(`¿Estás seguro de que deseas ${action} esta licencia?`)) {
                                                                        UpdateLicenseStatus(license.id, license.status);
                                                                    }
                                                                }}
                                                            >
                                                                {license.status === 1 ? 'Desactivar' : 'Activar'}
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            <Modal isOpen={modal} toggle={handleCloseModal} size='lg' centered className='border-0'>
                <ModalHeader toggle={handleCloseModal} className='p-3 ps-4 bg-soft-warning'>
                    Nueva Licencia
                </ModalHeader>
                <ModalBody className='p-4' style={{ overflow: 'visible' }}>
                    {errorMessage && (
                        <div className='text-danger bg-soft-danger mb-3 p-2 rounded'>
                            {errorMessage}
                        </div>
                    )}
                    <ModalCreateProjectLicensesSection
                        onChange={handleInputChange}
                        projectLicenses={createProjectLicenses}
                    />
                </ModalBody>
                <div className='modal-footer'>
                    <button type='button' className='btn btn-light w-xs' onClick={handleCloseModal}>
                        Cancelar
                    </button>
                    <button type='button' className='btn btn-warning w-xs' onClick={handleSubmit}>
                        Agregar
                    </button>
                </div>
            </Modal>
        </React.Fragment>
    );
};

export default ProjectLicensesTab;
